import axios from 'axios';

const TOKEN_KEY = 'bookdoc.corpDashboard.token';

// localStorage access throws outright in some privacy modes, so every read and
// write is guarded. A failure only costs us session persistence.
const readToken = () => {
    try {
        return window.localStorage.getItem(TOKEN_KEY);
    } catch (e) {
        return null;
    }
};

const writeToken = (token) => {
    try {
        window.localStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
        // Non-fatal: the session simply won't survive a reload.
    }
};

const clearToken = () => {
    try {
        window.localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
        // Nothing useful to do here.
    }
};

const friendly = (message) => Object.assign(new Error(message), { friendlyMessage: message });

// The API answers with three different error envelopes depending on which layer
// rejected the request:
//   401  { "errors": ["Invalid email or password."] }        array
//   401  { "errors": "Your account has not been verified" }  bare string
//   403  { "error":  "Corp dashboard is not enabled..." }    singular key
// Everything that displays an API failure goes through here.
const normaliseError = (error) => {
    if (error && error.friendlyMessage) return error.friendlyMessage;

    const response = error && error.response;
    const data = response && response.data;

    if (data) {
        if (Array.isArray(data.errors) && data.errors.length > 0) return data.errors.join(' ');
        if (typeof data.errors === 'string' && data.errors.trim().length > 0) return data.errors;
        if (typeof data.error === 'string' && data.error.trim().length > 0) return data.error;
    }

    if (response) {
        if (response.status === 429) return 'Too many attempts. Please wait a moment and try again.';
        if (response.status >= 500) return 'Something went wrong on our end. Please try again.';
        return 'Unable to sign in. Please try again.';
    }

    // No response at all: network failure, DNS, CORS, timeout.
    return 'Unable to reach BookDoc. Please check your connection and try again.';
};

const isUnauthorized = (error) => !!(error && error.response && error.response.status === 401);

const request = (options) => axios.request({
    baseURL: process.env.REACT_APP_API_DOMAIN,
    ...options
});

const authorized = (options) => {
    const token = readToken();
    if (!token) return Promise.reject(friendly('Your session has ended. Please sign in again.'));

    return request({
        ...options,
        headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` }
    });
};

const CorpDashboardApi = {
    normaliseError,
    isUnauthorized,
    isAuthenticated: () => !!readToken(),
    clearSession: clearToken,

    // Devise expects the credentials flat at the top level, not nested under
    // a `human_resource` key.
    login: (email, password) => {
        return request({
            method: 'post',
            url: '/api/corp_dashboard/login',
            headers: { 'Content-Type': 'application/json' },
            data: { email, password }
        }).then((response) => {
            const data = response.data || {};
            if (!data.auth_token) throw friendly('Signed in, but no session token was returned.');

            writeToken(data.auth_token);
            return data;
        });
    },

    // No start_date/end_date: the API's month-to-date default is what we want,
    // and passing an unparseable date 500s server-side.
    getDashboard: () => {
        return authorized({ method: 'get', url: '/api/corp_dashboard' })
            .then((response) => response.data)
            .catch((error) => {
                if (isUnauthorized(error)) clearToken();
                throw error;
            });
    },

    logout: () => {
        return authorized({ method: 'delete', url: '/api/corp_dashboard/logout' })
            .catch(() => null)          // Local sign-out must succeed regardless.
            .then(() => clearToken());
    }
};

export default CorpDashboardApi;
